import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    ProductTemplateListItem,
    ProductTypeAppStatusIcon,
    productTypeAppStatusText,
    useProductTemplateOutlet,
} from './OperatorProductTypes.tsx';
import {
    Box,
    InputAdornment,
    InputLabel,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { Localized, useLocalize } from 'dg-web-shared/lib/Localization.tsx';
import { DesignServices, FilterAlt } from '@mui/icons-material';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { intervalDurationName } from 'product-shared/product-template/SubscriptionIntervalDuration.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import * as NumberFormatter from 'dg-web-shared/lib/NumberFormatter.ts';
import { numberToPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import {
    MissingPermissionsText,
    ProductTemplateVehicleTypeField,
    ProductParkingaboRoleConfigField,
} from '../products/OperatorProductDetailModal.tsx';
import {
    CalendarDayReservationConfig,
    GuestDayPassConfig,
    ProductTemplateConfig,
    ProductTemplateConfigType,
    SubscriptionConfig,
} from 'product-shared/product-template/ProductTemplate.ts';
import { useCaseNameFromConfigType } from 'product-shared/product-template/ProductTemplateUseCase.ts';
import {
    quotaPartitionConfigText,
    QuotaPartitionReduction,
} from 'dg-web-shared/model/QuotaConfig.ts';
import { OperatorQuotaOutlet } from '../quotas/OperatorQuotasDetail.tsx';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { QuotaPartitionInfo } from '../common/state/PermitTypeState.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { css } from '@emotion/css';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';
import InfoIcon from '@mui/icons-material/Info';
import { isSubscriptionProRataEnabled } from 'product-shared/product-template/SubscriptionIntervalConfig.ts';
import { guestPassDurationCalculationModeName } from 'product-shared/product-template/ProductTemplateEnums.ts';

interface ProductTemplateDetail extends ProductTemplateListItem {
    validFrom: string;
    quotaPartitionInfo: QuotaPartitionInfo | null;
    vehicleTypes: LicensePlateType[];
}

export function OperatorProductTypesDetailModal() {
    const urlParams = useParams();
    const contractTemplateId = urlParams.contractTemplateId ?? null;

    const [productTemplateDetailState, productTemplateDetailRefetch] =
        useServerFetch<
            ProductTemplateDetail,
            { contractTemplateId: string | null },
            null
        >(
            ({ contractTemplateId }) => ({
                url: `/ui-api/operator-account/parkingabo/product-template-detail/${contractTemplateId}`,
            }),
            { contractTemplateId: contractTemplateId },
        );

    return (
        <>
            <OperatorRoutedModal
                variant={ModalVariant.STANDARD}
                backUrl={'..'}
                render={() => (
                    <OperatorAsyncLoadedSection
                        requestState={productTemplateDetailState}
                        render={productTemplateDetail => (
                            <OperatorProductTypesDetail
                                productTemplateDetail={productTemplateDetail}
                            />
                        )}
                    />
                )}
            />
            <OperatorQuotaOutlet
                context={{
                    refetchQuota: productTemplateDetailRefetch,
                }}
            />
        </>
    );
}

function OperatorProductTypesDetail({
    productTemplateDetail,
}: {
    productTemplateDetail: ProductTemplateDetail;
}) {
    const { currentLogin } = useOperatorContext();
    const quotasReadOnly = !currentLogin.permissions.quotasWrite;
    const navigate = useNavigate();
    const localize = useLocalize();
    const { showDtaAccount } = useProductTemplateOutlet();
    const config = productTemplateDetail.productTemplateConfig;
    const isBarrierGate = config.allowedZoneTypes.some(
        a => a === ZoneType.barrier_gate,
    );
    const quotaInfo = productTemplateDetail.quotaPartitionInfo;

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    Icon={DesignServices}
                    title={<Localized {...config.productName} />}
                    editMode={false}
                    buttons={
                        <HeaderActionCloseButtons
                            dropdownItems={
                                quotaInfo != null
                                    ? [
                                          {
                                              key: 'editQuota',
                                              label: (
                                                  <Localized
                                                      de="Kontingent bearbeiten"
                                                      fr="Modifier contingent"
                                                      it="Modifica contingente"
                                                      en="Edit quota"
                                                  />
                                              ),
                                              onClickCallback: () => {
                                                  if (quotaInfo == null) {
                                                      throw new Error(
                                                          'Quota can not be edited!',
                                                      );
                                                  }
                                                  navigate(
                                                      `quotas/${quotaInfo.contractQuotaPartitionId}`,
                                                  );
                                              },
                                              disabled: quotasReadOnly,
                                              tooltip: quotasReadOnly ? (
                                                  <MissingPermissionsText />
                                              ) : undefined,
                                          },
                                      ]
                                    : null
                            }
                            onClose={() => navigate('..')}
                        />
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <TextField
                            disabled
                            label={
                                <Localized
                                    de="Bezeichnung"
                                    fr="Description"
                                    it="Descrizione"
                                    en="Description"
                                />
                            }
                            value={localize(config.productName)}
                            sx={{
                                color: theme => theme.palette.common?.white,
                            }}
                        />
                        <TextField
                            disabled
                            multiline
                            label={
                                <Localized
                                    de="Zusatzinformationen"
                                    fr="Informations complémentaires"
                                    it="Informazioni supplementari"
                                    en="Additional information"
                                />
                            }
                            value={
                                config.additionalInfo
                                    ? localize(config.additionalInfo)
                                    : EmDash
                            }
                        />
                        <TextField
                            disabled
                            label={
                                <Localized
                                    de="Use case"
                                    fr="Cas d'utilisation"
                                    it="Caso d'uso"
                                    en="Use case"
                                />
                            }
                            value={localize(
                                useCaseNameFromConfigType(config.configType),
                            )}
                        />
                        <ProductTemplateZoneDescriptions
                            allowedZoneTypes={config.allowedZoneTypes}
                            zoneDescriptions={
                                productTemplateDetail.zoneDescriptions
                            }
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <TextField
                            disabled
                            label={
                                <Localized
                                    de="App-Kauf"
                                    fr="Achat dans l'app"
                                    it="Acquisto nell'app"
                                    en="App purchase"
                                />
                            }
                            value={localize(
                                productTypeAppStatusText(
                                    productTemplateDetail.appPurchaseVisibility,
                                    productTemplateDetail.validityStart,
                                    productTemplateDetail.validityEnd,
                                ),
                            )}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ProductTypeAppStatusIcon
                                                appPurchaseVisibility={
                                                    productTemplateDetail.appPurchaseVisibility
                                                }
                                                validityStart={
                                                    productTemplateDetail.validityStart
                                                }
                                                validityEnd={
                                                    productTemplateDetail.validityEnd
                                                }
                                            />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        {showDtaAccount && (
                            <TextField
                                disabled
                                label={
                                    <Localized
                                        de="Gruppe"
                                        fr="Groupe"
                                        it="Gruppo"
                                        en="Group"
                                    />
                                }
                                value={
                                    productTemplateDetail.dtaAccountDescription
                                }
                            />
                        )}
                        <ProductTypePrice config={config} />

                        <ProductTemplateVehicleTypeField
                            vehicleTypes={productTemplateDetail.vehicleTypes}
                        />

                        <ProductParkingaboRoleConfigField
                            config={config}
                            roles={productTemplateDetail.roleNames}
                        />

                        {config.configType ===
                            ProductTemplateConfigType.GUEST_DAY_PASS && (
                            <GuestDayPassConfigInfo config={config} />
                        )}
                        {quotaInfo && (
                            <QuotasInfoField
                                quotaInfo={quotaInfo}
                                isBarrierGate={isBarrierGate}
                            />
                        )}
                        {quotaInfo && quotaInfo.otherProducts.length > 0 && (
                            <TextField
                                disabled
                                multiline
                                label={
                                    <Localized
                                        de="Andere Produkttypen im Kontingent"
                                        fr="Autres types de produits dans le contingent"
                                        it="Altri tipi di prodotti nel contingente"
                                        en="Other product types in the quota"
                                    />
                                }
                                value={quotaInfo.otherProducts
                                    .map(product => localize(product.name))
                                    .join('\n')}
                            />
                        )}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'end',
                            }}
                        >
                            <BlockLabel
                                label={
                                    <Localized
                                        de="Aktualisiert am"
                                        fr="Actualisé le"
                                        it="Aggiornato il"
                                        en="Updated on"
                                    />
                                }
                            >
                                {Formatter.dayMonthYearHourMinute(
                                    DateTime.fromISO(
                                        productTemplateDetail.validFrom,
                                    ),
                                )}
                            </BlockLabel>
                            <BlockLabel label="ID">
                                {productTemplateDetail.contractTemplateId}
                            </BlockLabel>
                        </Box>
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function GuestDayPassConfigInfo({ config }: { config: GuestDayPassConfig }) {
    return (
        <>
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Verfügbare Optionen (Tage)"
                        fr="Options disponibles (jours)"
                        it="Opzioni disponibili (giorni)"
                        en="Available options (days)"
                    />
                }
                value={config.durationDayOptions.join(', ')}
            />
            <ReadOnlyTextField
                label={<Localized de="Modus" fr="Mode" it="Modo" en="Mode" />}
                value={
                    <Localized
                        {...guestPassDurationCalculationModeName(
                            config.durationCalculationMode,
                        )}
                    />
                }
            />
            <ReadOnlyTextField
                label={
                    <>
                        <Localized
                            de="Erste Ausfahrt"
                            fr="Première sortie"
                            it="Prima uscita"
                            en="First exit"
                        />
                        {config.allowFirstExitWithoutEntry && (
                            <Tooltip
                                title={
                                    <Localized
                                        de="Wenn der Benutzer mit dem Tagespass ausfährt, ohne ihn bei der Einfahrt verwendet zu haben, bleibt die andere Transaktion offen."
                                        fr="Si l'utilisateur sort avec le pass journalier sans l'avoir utilisé à l'entrée, l'autre transaction restera ouverte."
                                        it="Se l'utente esce con il pass giornaliero senza averlo usato all'entrata, l'altra transazione rimarrà aperta."
                                        en="If the user exits with the day pass without having used it upon entry, the other transaction will remain open."
                                    />
                                }
                                placement="right"
                            >
                                <InfoIcon
                                    sx={{
                                        color: theme => theme.palette.blue.main,
                                        marginLeft: 1,
                                    }}
                                />
                            </Tooltip>
                        )}
                    </>
                }
                value={
                    config.allowFirstExitWithoutEntry ? (
                        <Localized
                            de="auch möglich, wenn nicht mit dem Tagespass eingefahren"
                            fr="aussi possible, si non entré avec le pass journalier"
                            it="possibile anche se non entrato con il pass giornaliero"
                            en="also possible, if not entered with the daily pass"
                        />
                    ) : (
                        <Localized
                            de="nur möglich, wenn mit dem Tagespass eingefahren"
                            fr="uniquement possible si entré avec le pass journalier"
                            it="possibile solo se entrato con il pass giornaliero"
                            en="only possible, if entered with the daily pass"
                        />
                    )
                }
            />
        </>
    );
}

function QuotasInfoField({
    quotaInfo,
    isBarrierGate,
}: {
    quotaInfo: QuotaPartitionInfo;
    isBarrierGate: boolean;
}) {
    return (
        <ReadOnlyTextField
            label={
                <Localized
                    de="Kontingente"
                    fr="Contingents"
                    it="Contingenti"
                    en="Quotas"
                />
            }
            value={
                <>
                    {quotaInfo.quotaConfigs.map(quotaConfig => (
                        <Box key={quotaConfig.type}>
                            <Localized
                                {...quotaPartitionConfigText(
                                    quotaConfig,
                                    isBarrierGate,
                                )}
                            />
                            <QuotaReductionInfo
                                quotaLimit={quotaConfig.limit}
                                quotaReductions={quotaInfo.quotaReductions}
                            />
                        </Box>
                    ))}
                </>
            }
        />
    );
}

export function QuotaReductionInfo({
    quotaLimit,
    quotaReductions,
}: {
    quotaLimit: number;
    quotaReductions: QuotaPartitionReduction[];
}) {
    return (
        <>
            {quotaReductions.map(quotaReduction => {
                const validFrom = Formatter.dayMonthYear(
                    DateTime.fromISO(quotaReduction.validFrom),
                );
                const validTo = Formatter.dayMonthYear(
                    DateTime.fromISO(quotaReduction.validTo),
                );
                return (
                    <Box key={quotaReduction.createdAt}>
                        <Stack
                            direction={'row'}
                            sx={theme => ({
                                color: theme.palette.warning.main,
                                display: 'flex',
                                alignItems: 'center',
                            })}
                        >
                            <Box
                                sx={{
                                    width: '35px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <FilterAlt />
                            </Box>
                            <Box sx={{ width: '40px' }}>
                                {quotaLimit - quotaReduction.reductionCount}
                            </Box>
                            <Box>
                                <Localized
                                    de={`${validFrom} bis ${validTo}`}
                                    fr={`${validFrom} à ${validTo}`}
                                    it={`dal ${validFrom} al ${validTo}`}
                                    en={`from ${validFrom} to ${validTo}`}
                                />
                            </Box>
                        </Stack>
                    </Box>
                );
            })}
        </>
    );
}

export function ProductTemplateZoneDescriptions({
    allowedZoneTypes,
    zoneDescriptions,
}: {
    allowedZoneTypes: ZoneType[];
    zoneDescriptions: string[];
}) {
    return (
        <ReadOnlyTextField
            label={
                <ProductTemplateZoneDescriptionLabel
                    isBarrierGate={allowedZoneTypes.some(
                        a => a === ZoneType.barrier_gate,
                    )}
                    isSingleZone={zoneDescriptions.length <= 1}
                />
            }
            value={
                zoneDescriptions.length === 0 ? (
                    EmDash
                ) : (
                    <span className={css({ whiteSpace: 'pre-wrap' })}>
                        {zoneDescriptions.join('\n')}
                    </span>
                )
            }
        />
    );
}

function ProductTemplateZoneDescriptionLabel({
    isBarrierGate,
    isSingleZone,
}: {
    isBarrierGate: boolean;
    isSingleZone: boolean;
}) {
    if (isBarrierGate) {
        if (isSingleZone) {
            return (
                <Localized
                    de="Parking"
                    fr="Parking"
                    it="Parcheggio"
                    en="Parking"
                />
            );
        }
        return (
            <Localized
                de="Parkings"
                fr="Parkings"
                it="Parcheggi"
                en="Parkings"
            />
        );
    }
    if (isSingleZone) {
        return <Localized de="Zone" fr="Zone" it="Zona" en="Zone" />;
    }
    return <Localized de="Zonen" fr="Zones" it="Zone" en="Zones" />;
}

function ProductTypePrice({
    config,
}: {
    config: ProductTemplateConfig;
}): JSX.Element | null {
    switch (config.configType) {
        case ProductTemplateConfigType.SUBSCRIPTION:
            return <ProductTypeSubscriptionPrice config={config} />;
        case ProductTemplateConfigType.CALENDAR_DAY_RESERVATION:
            return <ProductTypeReservationPrice config={config} />;
        case ProductTemplateConfigType.GUEST_RESERVATION_UBS:
        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
        case ProductTemplateConfigType.ALL_ACCESS:
        case ProductTemplateConfigType.GUEST_PERMIT_VALID_TO:
        case ProductTemplateConfigType.GUEST_RESERVATION:
        case ProductTemplateConfigType.CUSTOMER_EXTERNAL:
        case ProductTemplateConfigType.EVENT:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
        case ProductTemplateConfigType.GUEST_DAY_PASS:
            return null;
    }
}

function ProductTypeSubscriptionPrice({
    config,
}: {
    config: SubscriptionConfig;
}) {
    const localize = useLocalize();
    const subscriptionProRataConfig =
        config.intervalConfig.subscriptionProRataConfig;
    return (
        <>
            <ReadOnlyTextField
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Localized
                            de="Preis / Einhheit"
                            fr="Prix / Unité"
                            it="Prezzo / Unità"
                            en="Price / Unit"
                        />
                        <Tooltip
                            title={
                                <>
                                    {isSubscriptionProRataEnabled(
                                        subscriptionProRataConfig,
                                    ) ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                            }}
                                        >
                                            <Box>
                                                <Localized
                                                    de="Das Abo wird pro Kalendermonat verrechnet. Sollten der erste und/oder der letzte Monat unvollständig sein, werden die Tage verrechnet."
                                                    fr="L'abonnement est facturé par mois civil. Si le premier et/ou le dernier mois sont incomplets, les jours seront facturés."
                                                    it="L'abbonamento viene conteggiato per mese di calendario. Qualora il primo e/o l'ultimo mese fossero parziali, verranno conteggiati i giorni."
                                                    en="The subscription is calculated per calendar month. If the first and/or last month are incomplete, the days will be counted."
                                                />
                                            </Box>
                                            <Box>
                                                <Localized
                                                    de="In der Parkingabo-App: kann ab dem folgenden Tag abgeschlossen werden, eine Kündigung ist jedoch nur zum Monatsende möglich."
                                                    fr="Dans l'app Parkingabo: peut être souscrit à partir du lendemain, tandis que la résiliation n'est possible qu'à la fin du mois."
                                                    it="Nell'app Parkingabo: può essere sottoscritto a partire dal giorno seguente, mentre la disdetta è possibile solo a fine mese."
                                                    en="In the Parkingabo app: can be started from the following day, while cancellation is only possible at the end of the month."
                                                />
                                            </Box>
                                            <Box>
                                                <Localized
                                                    de="Im Parkingportal: kann zu jedem beliebigen Datum gekündigt werden."
                                                    fr="Dans Parkingportal: peut être résilié à tout moment."
                                                    it="In Parkingportal: può essere disdetto per qualsiasi data."
                                                    en="In Parkingportal: can be cancelled for any date."
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                            }}
                                        >
                                            <Box>
                                                <Localized
                                                    de="Das Abo wird pro Kalendermonat verrechnet. Sollte der letzte Monat unvollständig sein, wird er nicht verrechnet."
                                                    fr="L'abonnement est facturé par mois civil. Si le dernier mois est incomplet, il ne sera pas facturé."
                                                    it="L'abbonamento viene conteggiato per mese di calendario. Qualora l'ultimo mese risultasse parziale, non verrà conteggiato."
                                                    en="The subscription is calculated per calendar month. If the last month is incomplete, it will not be charged."
                                                />
                                            </Box>
                                            <Box>
                                                <Localized
                                                    de="In der Parkingabo-App: kann ab dem folgenden Monat abgeschlossen werden, eine Kündigung ist nur zum Monatsende möglich."
                                                    fr="Dans l'app Parkingabo: peut être souscrit à partir du mois suivant, la résiliation n'est possible qu'à la fin du mois."
                                                    it="Nell'app Parkingabo: può essere sottoscritto a partire dal mese seguente, la disdetta è possibile solo a fine mese."
                                                    en="In the Parkingabo app: can be started from the following month, cancellation is only possible at the end of the month."
                                                />
                                            </Box>
                                            <Box>
                                                <Localized
                                                    de="Im Parkingportal: kann zu jedem beliebigen Datum gekündigt werden."
                                                    fr="Dans Parkingportal: peut être résilié à tout moment."
                                                    it="In Parkingportal: può essere disdetto per qualsiasi data."
                                                    en="In Parkingportal: can be cancelled for any date."
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            }
                            placement="right"
                        >
                            <InfoIcon
                                sx={{
                                    color: theme => theme.palette.blue.main,
                                    marginLeft: 1,
                                    fontSize: '25px',
                                }}
                            />
                        </Tooltip>
                    </Box>
                }
                value={
                    <>
                        <Box>{`${numberToPrice(config.intervalConfig.intervalPrice)} / ${localize(
                            intervalDurationName(
                                config.intervalConfig.intervalDuration,
                            ),
                        )}`}</Box>
                        {isSubscriptionProRataEnabled(
                            subscriptionProRataConfig,
                        ) && (
                            <Box>
                                {`${numberToPrice(subscriptionProRataConfig.dailyPrice)} / `}
                                <Localized
                                    de="Tag"
                                    fr="Jour"
                                    it="Giorno"
                                    en="Day"
                                />
                            </Box>
                        )}
                    </>
                }
            />
            <TextField
                disabled
                label={
                    <Localized
                        de="Max. Einheiten in der Zukunft"
                        fr="Max. unités dans le futur"
                        it="Max. unità nel futuro"
                        en="Max. units in the future"
                    />
                }
                value={config.intervalConfig.maxIntervalsPurchaseAheadInFuture}
            />
        </>
    );
}

function ProductTypeReservationPrice({
    config,
}: {
    config: CalendarDayReservationConfig;
}) {
    const localize = useLocalize();
    const timeAndPriceConfig = config.timeAndPriceConfig;
    const start = Formatter.hourMinute(
        DateTime.fromISO(timeAndPriceConfig.validityStart),
    );
    const end = Formatter.hourMinute(
        DateTime.fromISO(timeAndPriceConfig.validityEnd),
    );
    const mondayValue = timeAndPriceConfig.priceConfig.monday;
    const constantPrice = Object.values(timeAndPriceConfig.priceConfig).every(
        price => price == mondayValue,
    );

    return (
        <>
            <Stack spacing={2} direction="row">
                <TextField
                    disabled
                    label={
                        <Localized
                            de="Startzeit"
                            fr="Heure de début"
                            it="Ora d'inizio"
                            en="Start time"
                        />
                    }
                    value={start}
                />
                <TextField
                    disabled
                    label={
                        <Localized
                            de="Endzeit"
                            fr="Heure de fin"
                            it="Ora di fine"
                            en="End time"
                        />
                    }
                    value={end}
                />
            </Stack>
            <InputLabel>
                <Localized de="Preis" fr="Prix" it="Prezzo" en="Price" />
            </InputLabel>
            {constantPrice ? (
                <DayPriceRow
                    day={localize({
                        de: 'Montag-Sonnntag',
                        fr: 'Lundi-Dimanche',
                        it: 'Lunedì-Domenica',
                        en: 'Monday-Sunday',
                    })}
                    price={mondayValue}
                />
            ) : (
                <Box
                    sx={{
                        marginTop: '0px !important',
                    }}
                >
                    <DayPriceRow
                        day={localize({
                            de: 'Montag',
                            fr: 'Lundi',
                            it: 'Lunedì',
                            en: 'Monday',
                        })}
                        price={timeAndPriceConfig.priceConfig.monday}
                    />
                    <DayPriceRow
                        day={localize({
                            de: 'Dienstag',
                            fr: 'Mardi',
                            it: 'Martedì',
                            en: 'Tuesday',
                        })}
                        price={timeAndPriceConfig.priceConfig.tuesday}
                    />
                    <DayPriceRow
                        day={localize({
                            de: 'Mittwocht',
                            fr: 'Mercredi',
                            it: 'Mercoledì',
                            en: 'Wednesday',
                        })}
                        price={timeAndPriceConfig.priceConfig.wednesday}
                    />
                    <DayPriceRow
                        day={localize({
                            de: 'Donnerstag',
                            fr: 'Jeudi',
                            it: 'Giovedì',
                            en: 'Thursday',
                        })}
                        price={timeAndPriceConfig.priceConfig.thursday}
                    />
                    <DayPriceRow
                        day={localize({
                            de: 'Freitag',
                            fr: 'Vendredi',
                            it: 'Venerdì',
                            en: 'Friday',
                        })}
                        price={timeAndPriceConfig.priceConfig.friday}
                    />
                    <DayPriceRow
                        day={localize({
                            de: 'Samstag',
                            fr: 'Samedi',
                            it: 'Sabato',
                            en: 'Saturday',
                        })}
                        price={timeAndPriceConfig.priceConfig.saturday}
                    />
                    <DayPriceRow
                        day={localize({
                            de: 'Sonntag',
                            fr: 'Dimanche',
                            it: 'Domenica',
                            en: 'Sunday',
                        })}
                        price={timeAndPriceConfig.priceConfig.sunday}
                    />
                </Box>
            )}
        </>
    );
}

function DayPriceRow({ day, price }: { day: string; price: number | null }) {
    const priceString =
        price != null ? NumberFormatter.numberToPrice(price, true) : null;

    return (
        <Stack
            spacing={2}
            direction="row"
            sx={{
                color: theme => theme.palette.blue.main,
                fontWeight: '500',
                fontSize: '16px',
                marginTop: '0px !important',
            }}
        >
            <Box
                sx={{
                    width: '184px',
                }}
            >
                {day}
            </Box>
            {priceString ? (
                <Box>{priceString}</Box>
            ) : (
                <Box
                    sx={{
                        fontStyle: 'italic',
                        color: theme => theme.palette.grey['500'],
                    }}
                >
                    <Localized
                        de="Nicht verfügbar"
                        fr="Pas disponible"
                        it="Non disponibile"
                        en="Not available"
                    />
                </Box>
            )}
        </Stack>
    );
}
